/**
 * Shared formatting for a registered source's connection state.
 *
 * The "Connect to Data Room" popup and the Data Sources page describe a source
 * the same way, so the wording lives here rather than being written twice. The
 * tick means "this source has data", never "this account is authenticated" —
 * which is why `describeSource` always names the real state (a seeded row still
 * reads "Demo snapshot").
 *
 * **No item counts.** Epic locked decision 5: a connected source shows its
 * state and its freshness; how many items landed is backend-only health data.
 * A count in the UI is a number nobody can act on that reads as a completeness
 * guarantee — "1,240 items" says nothing about whether the one document you
 * need is among them, and it is wrong the moment a sync lands.
 */
import type { RegisteredSource, SourceStatus } from "../types/dataSource";

/**
 * What a source's state means to the user, which is not the same as the row's
 * stored `status`: "sync issue" is *derived* from a connected row whose data
 * has gone stale, never stored. Deriving it (rather than adding a stored state
 * a worker would have to remember to write) is the same choice the backend made
 * for stalled index rows — a connection that has stopped syncing cannot be
 * relied on to record that it stopped.
 */
export type SourceState = SourceStatus | "sync_issue";

export const STATUS_LABEL: Record<SourceState, string> = {
	demo_snapshot: "Demo snapshot",
	connected: "Connected",
	disconnected: "Not connected",
	sync_issue: "Sync issue — contact support",
};

/** A source carries data (and so earns a tick) unless it is disconnected. */
export function hasData(status: SourceStatus): boolean {
	return status !== "disconnected";
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * How old the last sync may get before "Connected" stops being true.
 *
 * 24h against a 15-minute sync cadence is ~96 consecutive missed cycles — far
 * past any transient failure, and the point at which the honest thing to say is
 * that something is wrong. The failure this exists for is silent: an OAuth
 * grant dies (the admin leaves, a password reset revokes it) and the firm reads
 * "Connected" over a dead sync indefinitely.
 */
export const SYNC_STALE_AFTER_MS = DAY_MS;

/** Compact relative age ("just now", "2h ago", "3d ago"). */
export function formatSyncedAgo(ms: number | null, now: number): string {
	if (!ms || !Number.isFinite(ms)) return "never synced";
	const delta = now - ms;
	if (delta < MINUTE_MS) return "synced just now";
	if (delta < HOUR_MS) return `synced ${Math.floor(delta / MINUTE_MS)}m ago`;
	if (delta < DAY_MS) return `synced ${Math.floor(delta / HOUR_MS)}h ago`;
	return `synced ${Math.floor(delta / DAY_MS)}d ago`;
}

/**
 * The state to render, derived from the row plus the clock.
 *
 * Only a *connected* row can go stale. A demo snapshot is a fixed artefact, not
 * a sync — its timestamp is when it was seeded and will be days old forever, so
 * ageing it into "Sync issue" would raise an alarm about a thing that is
 * working exactly as intended. A connected row that has never synced is stale
 * by the same rule: a connection that has produced nothing is not "Connected"
 * in any sense the user cares about.
 */
export function deriveSourceState(
	source: RegisteredSource,
	now: number
): SourceState {
	if (source.status !== "connected") return source.status;
	const last = source.last_synced_at_ms;
	if (!last || !Number.isFinite(last)) return "sync_issue";
	return now - last > SYNC_STALE_AFTER_MS ? "sync_issue" : "connected";
}

/** True when the source is connected but its sync has stopped keeping up. */
export function hasSyncIssue(source: RegisteredSource, now: number): boolean {
	return deriveSourceState(source, now) === "sync_issue";
}

/** "Connected · synced 2h ago" — the hover/focus detail. */
export function describeSource(source: RegisteredSource, now: number): string {
	const state = deriveSourceState(source, now);
	const label = STATUS_LABEL[state] ?? STATUS_LABEL.disconnected;
	return `${label} · ${formatSyncedAgo(source.last_synced_at_ms, now)}`;
}

/** Index registered rows by `source_system` for catalog lookup. */
export function indexBySourceSystem(
	rows: RegisteredSource[]
): Record<string, RegisteredSource> {
	const byId: Record<string, RegisteredSource> = {};
	for (const row of rows) {
		byId[row.source_system.toLowerCase()] = row;
	}
	return byId;
}
